/**
 * Prévia controlável (itens 4.1 a 4.3): o seu atleta voa pelo estádio com a simulação de `packages/sim`, a 30
 * ticks por segundo e desenho interpolado, com a Pelota, os dois Petardos e a Faísca.
 * - Treino: os outros 13 atletas ficam parados no ar; os companheiros que recebem a Pelota devolvem o passe.
 * - Partida (item 4.3/4.4): as regras valem (início, relógio, placar, faltas, pênalti, morte súbita). Contra
 *   "parados" os outros 13 ficam no lugar e só passam a bola quando a recebem (o aceite do 4.4); contra
 *   "robôs" eles jogam com os robôs simples de `packages/sim` (os bots de verdade chegam no item 5.x).
 * Câmera do 4.4 em `camera.ts`; anel no chão sob cada atleta para mostrar onde ele está e a que altura.
 */
import * as THREE from "three";
import { CLIPS, type ClipId } from "@besenliga/anim";
import { loadModels, type AthleteModels, type AthleteSpec } from "@besenliga/athlete-three";
import { randomAppearance } from "@besenliga/content";
import {
  ABILITIES,
  ABILITY_LABELS,
  ABILITY_OF,
  EXAMPLE_KITS,
  matchRating,
  FLIGHT,
  MATCH,
  NET,
  seedRng,
  type BotLevel,
  type GraphicsSettings,
  type Kit,
  type Position,
} from "@besenliga/shared";
import {
  BUTTON,
  FixedStepClock,
  Simulation,
  TICK_SECONDS,
  type AthleteState,
  type AthleteMatchStats,
  type InputCommand,
  type MatchState,
  emptyStats,
  type PlayEvent,
  type Team,
  type WorldState,
} from "@besenliga/sim";
import {
  SUN_DIRECTION,
  addStadiumLights,
  buildStadium,
  followSun,
  obstacleHeight,
  type Stadium,
} from "@besenliga/stadium-three";
import {
  BlobShadows,
  animateRider,
  createRider,
  playAction,
  updateRiderLod,
  type Rider,
} from "../stadium/riders";
import { BotTeam } from "@besenliga/bots";
import { PLAYER_ID, parkedId } from "./ids";
import { othersFor, pilotStats, previewSetup, type Other } from "./lineup";
export { othersFor, pilotStats };
import { Balls3D } from "./balls3d";
import { CAMERA, cameraOffset, clearDistance, easeDistance } from "./camera";
import { Controls, QUICK_CHAT, commandFrom } from "./controls";
import { HeightRings } from "./rings";
import { Markers } from "./markers";
import { simAccess, type Scenario, type WorldAccess } from "../training/scenario";
import type { RemoteSource } from "../online/connection";

export { PLAYER_ID } from "./ids";
export { PREVIEW_SPARK_SECONDS } from "./lineup";

/** Treino (sem regras), partida contra bots parados ou partida contra bots de um nível (item 5.1). */
export type PreviewMode = "treino" | "parados" | BotLevel;

/** Como a cena começa: modo, clubes, bots do treino (campo livre com bots) e roteiro (tutorial, desafios). */
export interface ViewOptions {
  mode: PreviewMode;
  clubs?: Clubs;
  /** No treino: os outros 13 jogam neste nível (sem regras). */
  trainingBots?: BotLevel | null;
  /** Na partida: nível dos bots de cada time (seus companheiros e o adversário), no lugar do nível único. */
  levels?: Record<Team, BotLevel> | undefined;
  scenario?: Scenario | null;
  /** Partida online: o servidor simula e a cena só desenha as fotos (item 6.1). */
  remote?: RemoteSource | null;
}

/** Aparência vinda de outra pessoa pela rede: só usa se tiver o formato do criador. */
function isLook(v: unknown): v is Omit<AthleteSpec, "posicao"> {
  if (typeof v !== "object" || v === null) return false;
  const l = v as Record<string, unknown>;
  return (
    (l.sexo === "homem" || l.sexo === "mulher") &&
    ["baixo", "medio", "alto"].includes(String(l.altura)) &&
    typeof l.biotipo === "string" &&
    typeof l.skinTone === "string" &&
    typeof l.hairStyle === "string" &&
    typeof l.hairColor === "string" &&
    typeof l.faceShape === "string" &&
    typeof l.beard === "boolean" &&
    typeof l.goggles === "boolean"
  );
}

/** Os dois times em campo: nome curto (placar, jogadas) e uniforme. O seu é sempre a casa. */
export type Clubs = Record<Team, { name: string; kit: Kit }>;

export const DEFAULT_CLUBS: Clubs = {
  casa: { name: "Corujas", kit: EXAMPLE_KITS["corujas-campinas"] },
  fora: { name: "Lobos", kit: EXAMPLE_KITS["lobos-guaiba"] },
};

/** Uma linha da tela de resultado. */
export interface ResultLine {
  id: string;
  team: Team;
  role: Position;
  you: boolean;
  stats: AthleteMatchStats;
  rating: number;
}

export interface PilotSetup {
  spec: AthleteSpec;
  kit: Kit;
  role: Position;
}

/** Clipe de animação de cada jogada, para quem a fez. */
const PLAY_CLIP: Partial<Record<PlayEvent["kind"], ClipId>> = {
  passe: "passe",
  arremesso: "arremesso",
  rebatida: "rebatida",
  defesa: "defesa",
  posse: "agarrar",
  captura: "mergulho",
  atordoado: "atordoado",
  queda: "atordoado",
};

/** Clipe da habilidade de cada posição (a Finta Espiral é um giro da vassoura, feito em `tick`). */
const ABILITY_CLIP: Record<Position, ClipId | null> = {
  artilheiro: null,
  goleiro: "defesa",
  batedor: "rebatida",
  cacador: "mergulho",
};

export interface FlightHud {
  stamina: number;
  exhausted: boolean;
  turbo: boolean;
  /** km/h */
  speed: number;
  height: number;
  role: Position;
  hasBall: boolean;
  /** 0 a 1 enquanto carrega o arremesso. */
  charge: number;
  stunned: boolean;
  score: Record<Team, number>;
  /** Últimas jogadas, da mais nova para a mais velha. */
  feed: { id: number; text: string; at: number }[];
  sparkActive: boolean;
  /** Habilidade: 0 a 1 de recarga (1 = pronta) e se está valendo agora. */
  ability: { ready: number; active: boolean };
  /** Regras da partida de teste (null no treino). */
  match:
    | (Pick<
        MatchState,
        "phase" | "phaseTicks" | "clockTicks" | "suddenDeath" | "suddenDeathTicks" | "winner" | "endReason"
      > & {
        penaltyTeam: Team | null;
      })
    | null;
  time: number;
  fps: number;
}

const angleLerp = (a: number, b: number, t: number) => {
  let d = b - a;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
};
const yawOf = (h: { x: number; z: number }) => Math.atan2(h.x, h.z);
/** Rapidez com que a correção do servidor some (1/s) e a partir de quanto ela vira um pulo (m). */
const SMOOTH_RATE = 10;
const SNAP_DISTANCE = 6;
const FINTA_TICKS = Math.round(ABILITIES.finta.seconds * NET.simTickHz);
/** Quanto tempo um companheiro segura a bola antes de devolver o passe (ticks). */
const RETURN_PASS_TICKS = 40;

export class FlightView {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(CAMERA.fov, 1, 0.5, 2000);
  /** Distância escolhida na roda do mouse e a distância atual (encurta perto de torres). */
  private zoom: number = CAMERA.distance;
  private camDistance: number = CAMERA.distance;
  private readonly rings = new HeightRings(14);
  private readonly stadium: Stadium;
  private readonly sun: THREE.DirectionalLight;
  private sim: Simulation;
  private readonly world: WorldAccess;
  private readonly clubs: Clubs;
  /** Roteiro do modo Treino (tutorial ou desafio), se houver. */
  readonly scenario: Scenario | null;
  private readonly markers = new Markers();
  private readonly remote: RemoteSource | null;
  /** Id do seu atleta (na partida online, a vaga que o servidor deu). */
  readonly me: string;
  private models: AthleteModels | null = null;
  private rosterVersion = -1;
  private readonly riderKeys = new Map<string, string>();
  /** Correção do servidor ainda por desfazer no desenho do seu atleta (m). */
  private smooth = { x: 0, y: 0, z: 0 };
  private readonly clock = new FixedStepClock();
  private readonly controls: Controls;
  private prev: WorldState;
  private readonly riders = new Map<string, Rider>();
  private readonly balls3d = new Balls3D();
  private blobs: BlobShadows | null = null;
  private readonly target = new THREE.Vector3();
  private readonly hand = new THREE.Vector3();
  private readonly names = new Map<string, string>();
  private feedId = 0;
  private camTheta = Math.PI;
  /** Direção da câmera travada para o direcional atual (ver `tick`). */
  private latchTheta = Math.PI;
  private dirKey = "";
  private heldTicks = 0;
  private frame = 0;
  private last = 0;
  private time = 0;
  private fpsFrames = 0;
  private fpsSince = 0;
  private readonly resizeObserver: ResizeObserver;
  private readonly others: Other[];
  /** Bots dos outros 13 (null: parados). */
  private readonly bots: BotTeam | null;
  /** Botões do seu último comando (para os parados saberem que você pediu a bola). */
  private myButtons = 0;
  readonly hud: FlightHud;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly settings: GraphicsSettings,
    private readonly pilot: PilotSetup,
    opts: ViewOptions = { mode: "treino" },
  ) {
    const mode = opts.mode;
    this.remote = opts.remote ?? null;
    this.me = this.remote?.you || PLAYER_ID;
    this.clubs = opts.clubs ?? DEFAULT_CLUBS;
    const clubs = this.clubs;
    this.scenario = opts.scenario ?? null;
    this.others = othersFor(pilot.role);
    const level = mode === "treino" ? (opts.trainingBots ?? null) : mode === "parados" ? null : mode;
    const levels = mode !== "treino" && mode !== "parados" ? opts.levels : undefined;
    this.bots = levels
      ? new BotTeam(this.others.map((o, i) => ({ id: parkedId(i), level: levels[o.team] })))
      : level
        ? new BotTeam(this.others.map((_, i) => ({ id: parkedId(i), level })))
        : null;
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: settings.antialias,
      powerPreference: "high-performance",
    });
    this.renderer.setPixelRatio(
      Math.min(window.devicePixelRatio, settings.maxPixelRatio) * settings.resolutionScale,
    );
    this.renderer.shadowMap.enabled = settings.shadowMapSize > 0;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.sun = addStadiumLights(this.scene, { shadowSize: settings.shadowMapSize });
    this.stadium = buildStadium({
      home: clubs.casa.kit,
      away: clubs.fora.kit,
      anisotropy: this.renderer.capabilities.getMaxAnisotropy(),
      animatedCrowd: settings.animatedCrowd,
      occupancy: settings.crowdOccupancy,
      trees: settings.trees,
    });
    this.scene.add(this.stadium.root, this.balls3d.root, this.rings.mesh);
    canvas.addEventListener("wheel", this.onWheel, { passive: false });
    this.stadium.bakeShadows(this.renderer, SUN_DIRECTION, settings.bakeSize);
    this.controls = new Controls(window, canvas);

    this.sim = new Simulation(previewSetup(pilot.role, pilotStats(pilot.spec), mode !== "treino"));
    this.world = simAccess(
      () => this.sim,
      (s) => {
        this.sim = s;
      },
      (w) => new Simulation(w),
    );
    this.scenario?.start(this.world);
    this.prev = this.sim.state;
    this.scene.add(this.markers.root);
    const me = this.sim.state.athletes.find((a) => a.id === this.me);
    if (me) this.camTheta = yawOf(me.heading) + Math.PI;
    this.names.set(this.me, "Você");
    this.hud = {
      stamina: 1,
      exhausted: false,
      turbo: false,
      speed: 0,
      height: 0,
      role: pilot.role,
      hasBall: false,
      charge: 0,
      stunned: false,
      score: { casa: 0, fora: 0 },
      feed: [],
      sparkActive: false,
      ability: { ready: 1, active: false },
      match: null,
      time: 0,
      fps: 0,
    };

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(canvas);
    this.resize();
    this.frame = requestAnimationFrame(this.tick);
  }

  async loadAthletes(modelsUrl: string): Promise<void> {
    const models = await loadModels(modelsUrl);
    this.models = models;
    if (this.remote) {
      this.syncRemoteRiders();
      if (this.settings.shadowMapSize === 0) {
        this.blobs = new BlobShadows(14);
        this.scene.add(this.blobs.mesh);
      }
      return;
    }
    this.addRider(
      this.me,
      createRider(models, { ...this.pilot.spec, posicao: this.pilot.role }, this.pilot.kit, this.settings),
    );
    this.others.forEach((p, i) => {
      const sexo = i % 2 === 0 ? "homem" : "mulher";
      const [, look] = randomAppearance(seedRng(500 + i), sexo);
      const spec: AthleteSpec = { sexo, altura: "medio", biotipo: "medio", posicao: p.pos, ...look };
      const kit = this.clubs[p.team].kit;
      this.addRider(parkedId(i), createRider(models, spec, kit, this.settings));
    });
    if (this.settings.shadowMapSize === 0) {
      this.blobs = new BlobShadows(this.riders.size);
      this.scene.add(this.blobs.mesh);
    }
  }

  /** Estado atual da simulação (para testes e para o painel). */
  get state(): Readonly<WorldState> {
    return this.remote?.latest() ?? this.sim.state;
  }

  /** Pula a etapa atual do tutorial. */
  skipStep(): void {
    this.scenario?.skip?.(this.world);
  }

  /** Pausa a simulação (menu de pausa); a cena continua desenhando. */
  paused = false;

  /** Linhas da tela de resultado: estatísticas e nota de cada atleta (null fora de partida). */
  results(): ResultLine[] | null {
    const m = this.sim.state.match;
    if (!m) return null;
    return this.sim.state.athletes.map((a) => {
      const stats = m.stats[a.id] ?? emptyStats();
      const other: Team = a.team === "casa" ? "fora" : "casa";
      const result =
        m.winner === "empate" || !m.winner ? "empate" : m.winner === a.team ? "vitoria" : "derrota";
      return {
        id: a.id,
        team: a.team,
        role: a.role,
        you: a.id === this.me,
        stats,
        rating: matchRating(a.role, stats, { result, conceded: m.goals[other] }),
      };
    });
  }

  dispose(): void {
    cancelAnimationFrame(this.frame);
    this.canvas.removeEventListener("wheel", this.onWheel);
    this.rings.dispose();
    this.markers.dispose();
    this.resizeObserver.disconnect();
    this.controls.dispose();
    this.blobs?.dispose();
    this.balls3d.dispose();
    this.stadium.dispose();
    this.renderer.dispose();
    this.renderer.forceContextLoss();
  }

  /** Roda do mouse: aproxima ou afasta a câmera, dentro da faixa. */
  private readonly onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    this.zoom = Math.min(CAMERA.maxDistance, Math.max(CAMERA.minDistance, this.zoom + e.deltaY * 0.02));
  };

  /** Chat rápido: por enquanto só aparece para você; no online (6.x) vai para o time. */
  private say(text: string): void {
    if (!text) return;
    this.hud.feed = [{ id: ++this.feedId, text: `Você: ${text}`, at: this.time }, ...this.hud.feed].slice(
      0,
      4,
    );
  }

  /**
   * Partida online: um atleta por vaga da escalação. Você com a aparência do criador; quem entrou com a dele (se
   * vier num formato que a cena entende); os bots com aparência sorteada pela vaga. Refaz quem mudou de dono.
   */
  private syncRemoteRiders(): void {
    const remote = this.remote;
    const models = this.models;
    if (!remote || !models || remote.rosterVersion === this.rosterVersion) return;
    this.rosterVersion = remote.rosterVersion;
    remote.roster.forEach((r, i) => {
      const key = r.id === this.me ? "voce" : r.human ? `gente:${JSON.stringify(r.look ?? null)}` : "bot";
      if (this.riderKeys.get(r.id) === key && this.riders.has(r.id)) return;
      const old = this.riders.get(r.id);
      if (old) {
        old.holder.removeFromParent();
        this.riders.delete(r.id);
      }
      const kit = this.clubs[r.team].kit;
      let spec: AthleteSpec | null = null;
      if (r.id === this.me) spec = { ...this.pilot.spec, posicao: r.role };
      else if (r.human && isLook(r.look)) spec = { ...r.look, posicao: r.role };
      if (!spec) {
        const sexo = i % 2 === 0 ? "homem" : "mulher";
        const [, look] = randomAppearance(seedRng(500 + i), sexo);
        spec = { sexo, altura: "medio", biotipo: "medio", posicao: r.role, ...look };
      }
      try {
        this.addRider(r.id, createRider(models, spec, kit, this.settings));
      } catch {
        // Aparência que não dá para montar: usa a sorteada.
        const [, look] = randomAppearance(seedRng(500 + i), "homem");
        this.addRider(
          r.id,
          createRider(
            models,
            { sexo: "homem", altura: "medio", biotipo: "medio", posicao: r.role, ...look },
            kit,
            this.settings,
          ),
        );
      }
      this.riderKeys.set(r.id, key);
    });
  }

  private addRider(id: string, r: Rider): void {
    this.riders.set(id, r);
    this.scene.add(r.holder);
  }

  private athlete(w: WorldState, id: string): AthleteState {
    const a = w.athletes.find((x) => x.id === id);
    if (!a) throw new Error(`Atleta ${id} não está na partida`);
    return a;
  }

  private resize(): void {
    const { clientWidth: w, clientHeight: h } = this.canvas;
    if (!w || !h) return;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  /** Comandos dos atletas parados: só quem está com a Pelota se mexe, vira para você e devolve o passe. */
  private parkedCommands(w: WorldState): Record<string, InputCommand> {
    const frame: Record<string, InputCommand> = {};
    const holder = w.balls?.pelota.holder;
    if (!holder || holder === this.me) {
      this.heldTicks = 0;
      return frame;
    }
    this.heldTicks++;
    const a = this.athlete(w, holder);
    const me = this.athlete(w, this.me);
    const mate = a.team === me.team;
    // Você pediu a bola (clique direito / B): o companheiro passa na hora.
    const called = mate && (this.myButtons & BUTTON.call) !== 0 && this.heldTicks > 3;
    const goal = mate ? me.position : { x: 0, y: 12, z: 0 };
    const dx = goal.x - a.position.x;
    const dz = goal.z - a.position.z;
    const l = Math.sqrt(dx * dx + dz * dz) || 1;
    // Um toque leve no direcional só para a vassoura virar.
    frame[holder] = {
      x: Math.round((dx / l) * 20),
      y: 0,
      z: Math.round((dz / l) * 20),
      buttons: this.heldTicks === RETURN_PASS_TICKS || called ? BUTTON.action : 0,
    };
    return frame;
  }

  private onPlays(plays: readonly PlayEvent[]): void {
    for (const p of plays) {
      const clip = PLAY_CLIP[p.kind];
      const r = this.riders.get(p.athlete);
      if (clip && r) playAction(r, CLIPS[clip], this.time);
      if (!this.sim.state.match) {
        if (p.kind === "gol" && p.team) this.hud.score[p.team] += 10;
        if (p.kind === "captura" && p.team) this.hud.score[p.team] += 30;
      }
      if (p.kind === "habilidade") {
        const a = this.state.athletes.find((x) => x.id === p.athlete);
        const ac = a ? ABILITY_CLIP[a.role] : null;
        if (ac && r) playAction(r, CLIPS[ac], this.time);
      }
      if (p.kind === "gol" && p.athlete) {
        const scorer = this.riders.get(p.athlete);
        if (scorer) playAction(scorer, CLIPS.comemorar, this.time + 0.3);
      }
      const text = this.describe(p);
      if (text) this.hud.feed = [{ id: ++this.feedId, text, at: this.time }, ...this.hud.feed].slice(0, 4);
    }
  }

  private who(id: string | undefined): string {
    if (!id) return "";
    if (id === this.me) return "Você";
    const a = this.state.athletes.find((x) => x.id === id);
    if (!a) return id;
    const team = this.clubs[a.team].name;
    const role = { artilheiro: "artilheiro", goleiro: "goleiro", batedor: "batedor", cacador: "caçador" }[
      a.role
    ];
    return `${role} dos ${team}`;
  }

  private describe(p: PlayEvent): string | null {
    const me = p.athlete === this.me;
    switch (p.kind) {
      case "gol":
        return `Gol dos ${p.team ? this.clubs[p.team].name : ""}! +10`;
      case "passe":
        return me ? `Você passou para o ${this.who(p.other)}` : null;
      case "arremesso":
        return me ? "Você arremessou" : null;
      case "posse":
        return me ? "A Pelota é sua" : null;
      case "roubo":
        return me ? `O ${this.who(p.other)} tomou a Pelota de você` : null;
      case "defesa":
        return `Defesa do ${this.who(p.athlete)}`;
      case "rebatida":
        return me
          ? p.other
            ? `Você rebateu o Petardo no ${this.who(p.other)}`
            : "Você rebateu o Petardo"
          : null;
      case "atordoado":
        return me ? "Você foi atingido por um Petardo" : `${this.who(p.athlete)} atordoado`;
      case "falta": {
        const kind = {
          empurrao: "empurrão",
          bloqueio: "bloqueio do aro",
          "petardo-goleiro": "Petardo no goleiro",
        }[p.foul ?? "empurrao"];
        return `Falta (${kind}) ${me ? "sua" : `do ${this.who(p.athlete)}`}`;
      }
      case "penalti":
        return `Pênalti para os ${p.team ? this.clubs[p.team].name : ""}`;
      case "morte-subita":
        return "Empate no tempo: morte súbita!";
      case "habilidade": {
        const a = this.state.athletes.find((x) => x.id === p.athlete);
        const name = a ? ABILITY_LABELS[ABILITY_OF[a.role]].name : "Habilidade";
        return me ? `Você usou ${name}` : null;
      }
      case "queda":
        return me ? "Mergulho baixo demais: você caiu!" : `${this.who(p.athlete)} caiu do mergulho`;
      case "aparece":
        return "A Faísca apareceu!";
      case "captura":
        return `${me ? "Você capturou" : `O ${this.who(p.athlete)} capturou`} a Faísca! +30`;
      default:
        return null;
    }
  }

  private readonly tick = (nowMs: number): void => {
    this.frame = requestAnimationFrame(this.tick);
    const now = nowMs / 1000;
    const dt = this.last ? Math.min(0.1, now - this.last) : 0;
    this.last = now;
    this.time += dt;

    // Simulação. O direcional é relativo à câmera. Para trás, a direção da câmera fica travada enquanto o
    // jogador mantém o direcional: segurar "para trás" faz uma meia-volta suave e termina voando para lá, em
    // vez de girar sem fim atrás de uma câmera que acompanha a vassoura. Para a frente e para os lados a
    // câmera vale ao vivo, então segurar A ou D faz uma curva contínua.
    const ticks = this.paused ? 0 : this.clock.advance(dt);
    let alpha = this.clock.alpha;
    let cur: WorldState = this.sim.state;
    /** Fração entre ticks do seu atleta (online ele segue o relógio local, não o das fotos). */
    let ownAlpha: number | null = null;
    if (this.remote) {
      // Online: os comandos vão para o servidor a cada tick, e a cena desenha as fotos que chegam.
      this.syncRemoteRiders();
      const latest = this.remote.latest();
      for (let i = 0; i < ticks; i++) {
        const intent = this.controls.intent(latest?.balls?.pelota.holder === this.me);
        const camForward = { x: -Math.sin(this.camTheta), z: -Math.cos(this.camTheta) };
        if (intent.chat === 0) intent.call = true;
        this.remote.send(commandFrom(intent, camForward));
        if (intent.chat !== undefined) this.say(QUICK_CHAT[intent.chat] ?? "");
      }
      this.onPlays(this.remote.takePlays());
      const fr = this.remote.frame(nowMs);
      if (!fr) {
        this.renderer.render(this.scene, this.camera);
        return;
      }
      this.prev = fr.prev;
      cur = fr.cur;
      alpha = fr.alpha;
      // Seu atleta vem da predição (item 6.2), sem o atraso da rede; as correções do servidor somem aos poucos.
      const pr = this.remote.predicted();
      if (pr) {
        const c = this.remote.takeCorrection();
        this.smooth.x += c.x;
        this.smooth.y += c.y;
        this.smooth.z += c.z;
        const decay = Math.exp(-SMOOTH_RATE * dt);
        this.smooth.x *= decay;
        this.smooth.y *= decay;
        this.smooth.z *= decay;
        // Correção grande (atordoado, pênalti, gol): melhor pular de uma vez.
        if (Math.abs(this.smooth.x) + Math.abs(this.smooth.y) + Math.abs(this.smooth.z) > SNAP_DISTANCE)
          this.smooth = { x: 0, y: 0, z: 0 };
        const shift = (a: AthleteState): AthleteState => ({
          ...a,
          position: {
            x: a.position.x + this.smooth.x,
            y: a.position.y + this.smooth.y,
            z: a.position.z + this.smooth.z,
          },
        });
        const myPrev = shift(pr.prev);
        const myCur = shift(pr.cur);
        this.prev = { ...fr.prev, athletes: fr.prev.athletes.map((a) => (a.id === this.me ? myPrev : a)) };
        cur = { ...fr.cur, athletes: fr.cur.athletes.map((a) => (a.id === this.me ? myCur : a)) };
        ownAlpha = this.clock.alpha;
      }
    }
    for (let i = 0; i < (this.remote ? 0 : ticks); i++) {
      this.prev = this.sim.state;
      const holding = this.prev.balls?.pelota.holder === this.me;
      const intent = this.controls.intent(holding);
      const dirKey = `${Math.round(intent.forward * 4)},${Math.round(intent.right * 4)}`;
      if (dirKey !== this.dirKey) {
        this.dirKey = dirKey;
        this.latchTheta = this.camTheta;
      }
      const theta = intent.forward < -0.1 ? this.latchTheta : this.camTheta;
      const camForward = { x: -Math.sin(theta), z: -Math.cos(theta) };
      const frame = this.bots ? this.bots.inputs(this.prev) : this.parkedCommands(this.prev);
      if (this.scenario?.inputs) Object.assign(frame, this.scenario.inputs(this.prev));
      // "Passa!" no chat rápido também pede a bola.
      if (intent.chat === 0) intent.call = true;
      const mine = commandFrom(intent, camForward);
      frame[this.me] = mine;
      this.myButtons = mine.buttons;
      if (intent.chat !== undefined) this.say(QUICK_CHAT[intent.chat] ?? "");
      this.sim.step(frame);
      const plays = this.sim.state.plays;
      this.onPlays(plays);
      this.scenario?.afterStep(this.world, plays);
    }
    if (this.scenario) this.markers.update(this.scenario.markers(this.sim.state), this.time);
    if (!this.remote) {
      alpha = this.clock.alpha;
      cur = this.sim.state;
    }

    // Desenho: interpola entre o tick anterior e o atual.
    for (const a of cur.athletes) {
      const r = this.riders.get(a.id);
      if (!r) continue;
      const p = this.prev.athletes.find((x) => x.id === a.id) ?? a;
      const al = a.id === this.me && ownAlpha !== null ? ownAlpha : alpha;
      r.holder.position.set(
        p.position.x + (a.position.x - p.position.x) * al,
        p.position.y + (a.position.y - p.position.y) * al,
        p.position.z + (a.position.z - p.position.z) * al,
      );
      const y0 = yawOf(p.heading);
      const y1 = yawOf(a.heading);
      const yaw = angleLerp(y0, y1, al);
      const turnRate = angleLerp(0, y1 - y0, 1) / TICK_SECONDS;
      const speed = Math.sqrt(a.velocity.x * a.velocity.x + a.velocity.z * a.velocity.z);
      // Aceleração na direção da vassoura: positiva arrancando, negativa freando.
      const along = a.velocity.x * a.heading.x + a.velocity.z * a.heading.z;
      const alongBefore = p.velocity.x * a.heading.x + p.velocity.z * a.heading.z;
      const accel = (along - alongBefore) / TICK_SECONDS;
      // Finta Espiral: um giro completo da vassoura enquanto dura.
      const finta = ABILITY_OF[a.role] === "finta" && a.abilityTicks > 0;
      const spin = finta ? (1 - (a.abilityTicks - alpha) / FINTA_TICKS) * Math.PI * 2 : 0;
      animateRider(
        r,
        this.time,
        yaw,
        { turnRate, climb: a.velocity.y, speed, turbo: a.turbo, accel, spin },
        dt,
      );
    }
    this.blobs?.update([...this.riders.values()]);
    this.rings.update(
      cur.athletes.flatMap((a) => {
        const r = this.riders.get(a.id);
        return r ? [{ position: r.holder.position, team: a.team, mine: a.id === this.me }] : [];
      }),
    );

    // Pelota na mão de quem carrega.
    let held: THREE.Vector3 | undefined;
    const holderId = cur.balls?.pelota.holder;
    const handBone = holderId ? this.riders.get(holderId)?.rig.bones["hand_r"] : undefined;
    if (handBone) held = handBone.getWorldPosition(this.hand);
    if (cur.balls && this.prev.balls) this.balls3d.update(this.prev.balls, cur.balls, alpha, this.time, held);

    // Câmera atrás e acima do seu atleta, virando com atraso para a direção da vassoura.
    const me = this.athlete(cur, this.me);
    const mine = this.riders.get(this.me);
    const focus = mine
      ? mine.holder.position
      : new THREE.Vector3(me.position.x, me.position.y, me.position.z);
    const meSpeed = Math.sqrt(me.velocity.x * me.velocity.x + me.velocity.z * me.velocity.z);
    this.camTheta = angleLerp(this.camTheta, yawOf(me.heading) + Math.PI, 1 - Math.exp(-CAMERA.lag * dt));
    this.target.lerp(focus, dt > 0 ? 1 - Math.exp(-10 * dt) : 1);
    // Atrás e acima, a 55°; mais perto quando uma torre ficaria entre a câmera e você.
    const wanted = clearDistance(this.target, this.camTheta, this.zoom, obstacleHeight);
    // Para dentro na hora (nunca atravessa a torre); para fora, devagar.
    this.camDistance = Math.min(easeDistance(this.camDistance, wanted, dt), wanted);
    const off = cameraOffset(this.camTheta, this.camDistance);
    this.camera.position.set(this.target.x + off.x, this.target.y + off.y, this.target.z + off.z);
    this.camera.lookAt(this.target.x, this.target.y + 2, this.target.z);
    followSun(this.sun, this.target, 45);
    for (const r of this.riders.values()) updateRiderLod(r, this.camera, this.settings);
    this.stadium.update(this.time);
    this.renderer.render(this.scene, this.camera);

    this.hud.stamina = me.stamina;
    this.hud.exhausted = me.exhausted;
    this.hud.turbo = me.turbo;
    this.hud.speed = Math.round(meSpeed * 3.6);
    this.hud.height = Math.round(me.position.y - FLIGHT.minHeight);
    this.hud.hasBall = holderId === this.me;
    this.hud.charge = Math.min(1, me.chargeTicks / NET.simTickHz);
    this.hud.stunned = me.stunTicks > 0;
    this.hud.sparkActive = cur.balls?.faisca.active ?? false;
    this.hud.ability = {
      ready: 1 - me.abilityCooldown / (MATCH.abilityCooldownSeconds * NET.simTickHz),
      active: me.abilityTicks > 0,
    };
    const m = cur.match;
    if (m) {
      this.hud.score = { ...m.score };
      this.hud.match = {
        phase: m.phase,
        phaseTicks: m.phaseTicks,
        clockTicks: m.clockTicks,
        suddenDeath: m.suddenDeath,
        suddenDeathTicks: m.suddenDeathTicks,
        winner: m.winner,
        endReason: m.endReason,
        penaltyTeam: m.penalty?.team ?? null,
      };
    }
    this.hud.time = this.time;
    this.fpsFrames++;
    if (now - this.fpsSince >= 1) {
      this.hud.fps = Math.round(this.fpsFrames / (now - this.fpsSince));
      this.fpsFrames = 0;
      this.fpsSince = now;
    }
  };
}
